/**
 * What each connector offers, for the Agents page's forms. Keep in step with the harness
 * connectors (agents/src/connectors/*.ts: capabilities and their params) and datahub's
 * event connectors (src/services/agents/events.py, approvals.py: event types).
 */

export type Field = { key: string; label: string; generated?: boolean };
export type Param = { key: string; label: string; kind: 'text' | 'list'; placeholder?: string; required?: boolean };
export type CapabilitySpec = { capability: string; label: string; params: Param[] };

// `pliable` is Pliable itself: every tenant has it, with no connection to add.
export const PLIABLE = 'pliable';

export const CONNECTORS: Record<string, { label: string; fields: Field[]; hint: string }> = {
  datadog: {
    label: 'Datadog',
    fields: [
      { key: 'api_key', label: 'API key' },
      { key: 'app_key', label: 'Application key (read-only scopes)' },
      { key: 'webhook_secret', label: 'Webhook secret (copy it into the webhook’s X-Plb-Webhook-Secret header)', generated: true },
    ],
    hint: 'Alerts arrive through a Datadog webhook to the delivery URL.',
  },
  github: {
    label: 'GitHub',
    fields: [{ key: 'token', label: 'Fine-grained token (read-only, only the repos agents may read)' }],
    hint: 'Agents read code through GitHub’s MCP server, read-only: through the Pliable GitHub app, or a token you create.',
  },
  linear: {
    label: 'Linear',
    fields: [{ key: 'api_key', label: 'API key' }],
    hint: 'Tickets and comments post as the Pliable Linear app, or, with an API key, as the key’s owner.',
  },
  slack: {
    label: 'Slack',
    fields: [
      { key: 'bot_token', label: 'Bot token (xoxb-…)' },
      { key: 'signing_secret', label: 'Signing secret' },
    ],
    hint: 'One Slack app per bot. Set its Event Subscriptions and Interactivity request URLs to the delivery URL (see Docs → Agent Connections → Slack).',
  },
};

const teams = (label = 'Linear teams (keys, e.g. ENG)'): Param => ({ key: 'teams', label, kind: 'list', placeholder: 'ENG, OPS' });

export const CAPABILITIES: Record<string, CapabilitySpec[]> = {
  // Who sees an artifact, and who gets an email, are these settings, never the run's choice
  // (src/api/routers/agent_tools.py): people in this tenant, by email.
  [PLIABLE]: [
    { capability: 'ellie.ask', label: 'Ask Ellie about the data', params: [] },
    {
      capability: 'artifact.create',
      label: 'Save artifacts',
      params: [{ key: 'share_with', label: 'Share with (emails of people here)', kind: 'list', placeholder: 'ana@acme.com, raj@acme.com' }],
    },
    {
      capability: 'email.send',
      label: 'Send email',
      params: [{ key: 'to', label: 'To (emails of people here)', kind: 'list', placeholder: 'ana@acme.com', required: true }],
    },
  ],
  datadog: [
    {
      capability: 'logs.read',
      label: 'Read logs, monitors, events and metrics',
      params: [
        { key: 'site', label: 'Datadog site', kind: 'text', placeholder: 'datadoghq.com' },
        { key: 'tools', label: 'Only these tools (default: the core reads)', kind: 'list', placeholder: 'search_datadog_logs' },
      ],
    },
  ],
  github: [
    {
      capability: 'code.read',
      label: 'Read code (read-only)',
      params: [
        { key: 'repos', label: 'Repositories (required with the Pliable GitHub app)', kind: 'list', placeholder: 'ws-data-hub' },
        { key: 'toolsets', label: 'Toolsets', kind: 'list', placeholder: 'repos, pull_requests' },
      ],
    },
  ],
  linear: [
    { capability: 'ticket.search', label: 'Search tickets', params: [teams('Search these teams (blank: all)')] },
    { capability: 'ticket.link', label: 'Link an existing ticket', params: [{ ...teams(), required: true }] },
    {
      capability: 'ticket.create',
      label: 'Create tickets',
      params: [
        { key: 'team', label: 'In team (key)', kind: 'text', placeholder: 'ENG', required: true },
        { key: 'labels', label: 'Labels it may set', kind: 'list', placeholder: 'Bug, Improvement' },
      ],
    },
    { capability: 'ticket.comment', label: 'Comment on linked tickets', params: [] },
    { capability: 'ticket.status', label: 'Change linked tickets’ status', params: [] },
  ],
  slack: [
    { capability: 'message.reply', label: 'Reply in the thread it was asked in', params: [] },
    {
      capability: 'message.post',
      label: 'Post in a channel',
      params: [{ key: 'channel', label: 'Channel ID (invite the bot to it first)', kind: 'text', placeholder: 'C0123456789', required: true }],
    },
  ],
};

// What a step reads of each event (and a test sends): the connector's payload as the router
// gets it (src/services/agents/events.py DatadogConnector, approvals.py SlackConnector.handle).
const DATADOG_ALERT = {
  id: '1234567890', title: '[Triggered] CPU high on web-1', date: '1767225600000', transition: 'Triggered',
  alert_id: '987654', priority: 'P2', link: 'https://app.datadoghq.com/monitors/987654', tags: 'env:prod,service:web', body: 'CPU above 90% for 5 minutes',
};

/** What starts a step: events by connector, and a person running it by hand. */
export const EVENTS: Record<string, { event: string; label: string; sample: Record<string, unknown> }[]> = {
  datadog: [
    { event: 'alert.triggered', label: 'An alert fires', sample: DATADOG_ALERT },
    { event: 'alert.recovered', label: 'An alert recovers', sample: { ...DATADOG_ALERT, title: '[Recovered] CPU high on web-1', transition: 'Recovered' } },
  ],
  slack: [
    {
      event: 'message.received',
      label: 'Someone @mentions or DMs the bot',
      sample: { text: 'Why did signups drop yesterday?', user: 'U0123456789', channel: 'C0123456789', channel_type: 'channel', ts: '1767225600.000100', thread_ts: null },
    },
  ],
};
export const RUN_BY_HAND = { source: 'user', event: 'run.requested' } as const;
/** A new schedule: Mondays at 9am where the person editing is. */
export const runOnSchedule = (): Trigger => ({
  source: 'schedule',
  event: 'schedule.fired',
  cron: '0 9 * * 1',
  timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
});
/** A scheduled run's payload (src/services/agents/events.py start_scheduled_run), for tests. */
export const scheduleSample = (t: Trigger) => ({ scheduled_for: new Date().toISOString(), cron: t.cron, timezone: t.timezone || 'UTC' });

/** How a trigger reads in lists: "run by hand", "on schedule 0 9 * * 1 (America/New_York)", "ops-datadog · alert.triggered". */
export const triggerLabel = (t: Trigger, eventLabel?: string) =>
  t.source === 'user'
    ? 'run by hand'
    : t.source === 'schedule'
      ? `on schedule ${t.cron} (${t.timezone || 'UTC'})`
      : `${t.connection} · ${eventLabel ?? t.event}`;

export const MODELS = [
  { value: 'opus', label: 'Opus (most capable)' },
  { value: 'sonnet', label: 'Sonnet (balanced)' },
  { value: 'haiku', label: 'Haiku (fast, cheap)' },
];

// The team document datahub reads and writes (src/services/agents/teams.py team_json / put_team).
export type Trigger = {
  source?: 'connection' | 'user' | 'schedule';
  connection?: string | null;
  event: string;
  cron?: string | null; // source schedule
  timezone?: string | null; // source schedule; UTC when unset
  filter?: Record<string, unknown>;
  work_item_key?: string | null;
};
export type Capability = { connection: string; capability: string; params: Record<string, unknown> };
export type Member = {
  id?: string;
  name: string;
  prompt: string;
  model: string | null;
  budget_usd: number | null;
  triggers: Trigger[];
  capabilities: Capability[];
  output_fields: Record<string, string[]>;
  sandbox_image?: string | null;
  skills?: string[];
};
// No connection: an Admin approves in Pliable only. With one, also in that Slack bot's channel.
export type Gate = { connection?: string | null; channel?: string | null };
export type Edge = { from: string; when: Record<string, unknown>; gate?: Gate | null; to: string };
export type TeamDoc = { id?: string; name: string; description: string; members: Member[]; edges: Edge[]; live_version?: number | null };
export type Connection = { id: string; name: string; connector: string; delivery_url: string | null; auth_method?: string; config?: Record<string, unknown> };

export const emptyMember = (n: number): Member => ({
  name: `step-${n}`,
  prompt: '',
  model: 'sonnet',
  budget_usd: 1,
  triggers: [],
  capabilities: [],
  output_fields: {},
});

/** "a, b" ⇄ ["a", "b"]. */
export const toList = (text: string) =>
  text
    .split(',')
    .map((s) => s.trim())
    .filter(Boolean);
export const fromList = (value: unknown) => (Array.isArray(value) ? value.join(', ') : typeof value === 'string' ? value : '');

const filled = <V,>(o: Record<string, V> | undefined) => Object.fromEntries(Object.entries(o ?? {}).filter(([k]) => k.trim()));

/** The document to save: half-filled rows (an empty condition or field name) dropped. */
export const cleaned = (doc: TeamDoc): TeamDoc => ({
  ...doc,
  members: doc.members.map((m) => ({
    ...m,
    triggers: m.triggers.map((t) => ({ ...t, filter: filled(t.filter) })),
    capabilities: m.capabilities.filter((c) => c.connection && c.capability),
    output_fields: filled(m.output_fields),
  })),
  edges: doc.edges.map((e) => ({ ...e, when: filled(e.when) })),
});
